import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import EditIcon from '@mui/icons-material/Edit';
import unifiedApi from '../../utils/unifiedApi';
import {
  EMERGENCY_CONTACT_NAME_MAX,
  EMERGENCY_CONTACT_PHONE_ERROR,
  EMERGENCY_CONTACT_PHONE_MAX,
  formatPhoneForDisplay,
  isValidEmergencyContactPhone,
  notifyEmergencyContactUpdated,
} from '../../utils/emergencyContact';

/**
 * Emergency contact on the player profile. The parent decides visibility; the API
 * enforces it too (self or CanViewAdminData). Only the player can edit.
 */
const EmergencyContactSection = ({ userTenantId, canEdit }) => {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ emergencyContactName: '', emergencyContactPhone: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setHidden(false);
    setEditing(false);
    (async () => {
      try {
        const data = await unifiedApi.getEmergencyContact(userTenantId);
        if (!cancelled) setContact(data || {});
      } catch {
        // Forbidden or failed — don't surface the section at all.
        if (!cancelled) setHidden(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userTenantId]);

  const nameError = touched && !form.emergencyContactName.trim() ? 'Name is required.' : null;
  const phoneError = touched
    ? (!form.emergencyContactPhone.trim()
      ? 'Phone number is required.'
      : (!isValidEmergencyContactPhone(form.emergencyContactPhone) ? EMERGENCY_CONTACT_PHONE_ERROR : null))
    : null;

  const startEdit = () => {
    setForm({
      emergencyContactName: contact?.emergencyContactName || '',
      emergencyContactPhone: contact?.emergencyContactPhone || '',
    });
    setTouched(false);
    setSaveError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    setTouched(true);
    if (!form.emergencyContactName.trim() || !isValidEmergencyContactPhone(form.emergencyContactPhone)) return;
    const payload = {
      emergencyContactName: form.emergencyContactName.trim(),
      emergencyContactPhone: form.emergencyContactPhone.trim(),
    };
    setSaving(true);
    setSaveError(null);
    try {
      await unifiedApi.updateEmergencyContact(userTenantId, payload);
      setContact(payload);
      setEditing(false);
      notifyEmergencyContactUpdated();
    } catch (err) {
      setSaveError(err?.message || 'Failed to save emergency contact.');
    } finally {
      setSaving(false);
    }
  };

  if (hidden) return null;

  const isMissing = !contact?.emergencyContactName || !contact?.emergencyContactPhone;

  return (
    <Box sx={{ mt: 3 }} data-testid="emergency-contact-section">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <ContactEmergencyIcon sx={{ mr: 1 }} />
          Emergency Contact
        </Typography>
        {canEdit && !editing && !loading && (
          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={startEdit}>
            {isMissing ? 'Add Emergency Contact' : 'Edit Emergency Contact'}
          </Button>
        )}
      </Stack>

      {loading ? (
        <CircularProgress size={20} />
      ) : editing ? (
        <Stack spacing={2} sx={{ maxWidth: { xs: '100%', sm: 480 } }}>
          {saveError && <Alert severity="error">{saveError}</Alert>}
          <TextField
            label="Emergency Contact Name"
            value={form.emergencyContactName}
            onChange={(e) => setForm((prev) => ({ ...prev, emergencyContactName: e.target.value }))}
            inputProps={{ maxLength: EMERGENCY_CONTACT_NAME_MAX }}
            error={!!nameError}
            helperText={nameError}
            required
            size="small"
            fullWidth
          />
          <TextField
            label="Emergency Contact Number"
            type="tel"
            value={form.emergencyContactPhone}
            onChange={(e) => setForm((prev) => ({ ...prev, emergencyContactPhone: e.target.value }))}
            inputProps={{ maxLength: EMERGENCY_CONTACT_PHONE_MAX }}
            error={!!phoneError}
            helperText={phoneError}
            required
            size="small"
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outlined" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      ) : isMissing ? (
        <Typography variant="body1" color="text.secondary">
          Not provided
        </Typography>
      ) : (
        <Box>
          <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
            {contact.emergencyContactName}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {formatPhoneForDisplay(contact.emergencyContactPhone)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EmergencyContactSection;
